import React, { useEffect, useState } from 'react';
import CustomModal from '../../../components/customModal/CustomModal';
import { Button, Form, Spinner, Table } from 'react-bootstrap';
import useEcwOrders from '../../../Hooks/useEcwOrders';
import { EcwEncounterDetails } from '../EcwConstants';
import { formatDate } from '../../../utils/commonUtils';

interface IEcwEncountersModalProps {
    isOpen: boolean;
    onClose: () => void;
    patientId: string;
    onEncounterSelect: (encounter: EcwEncounterDetails | null) => void;
    encounterSubmissionError: string;
}

const EcwEncountersModal: React.FC<IEcwEncountersModalProps> = ({ isOpen, onClose, patientId, onEncounterSelect, encounterSubmissionError }) => {
    const [encounterList, setEncounterList] = useState<EcwEncounterDetails[]>([]);
    const [submissionErr, setSubmissionErr] = useState<string>('');
    const [selectedEncounter, setSelectedEncounter] = useState<EcwEncounterDetails | null>(null);
    const { getAllEncountersForPatientAPI, getAllEncountersForPatientLoading } = useEcwOrders();

    useEffect(() => {
        setSubmissionErr(encounterSubmissionError);
    }, [encounterSubmissionError]);

    const fetchEncounterList = async () => {
        try {
            const res = await getAllEncountersForPatientAPI({ patientId });
            if (res?.encounters?.length > 0) {
                setEncounterList(res.encounters);
            }
        } catch (error) {
            console.error("Error fetching encounter list:", error);
        }
    };

    useEffect(() => {
        if (patientId) fetchEncounterList();
    }, [patientId]);

    const handleEncounterChange = (encounter: EcwEncounterDetails) => {
        setSubmissionErr('');
        setSelectedEncounter(encounter);
    }

    const handleSubmit = () => {
        setSubmissionErr('');
        onEncounterSelect(selectedEncounter);
    }

    if (!isOpen) return null;

    return (
        <CustomModal
            title='Select Encounter'
            onClose={onClose}
            overlayClass='ecw-encounterList-modal'
            content={
                <>
                    <div className="ecw-encounterList-modal__table-wrapper">
                        <Table bordered={false} hover className="m-0">
                            <thead>
                                <tr>
                                    <th className='text-center'>Select</th>
                                    <th>Encounter Code</th>
                                    <th>Encounter Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {getAllEncountersForPatientLoading ? (
                                    <tr>
                                        <td colSpan={3} className='text-center'>
                                            <Spinner animation="border" size="sm" variant="primary" /> Loading...
                                        </td>
                                    </tr>
                                ) : encounterList?.length === 0 ? (
                                    <tr>
                                        <td colSpan={3} className='text-center'>No encounters found.</td>
                                    </tr>
                                ) : (
                                    encounterList?.map((encounter, index) => (
                                        <tr key={index} className={encounter.encounterId === selectedEncounter?.encounterId ? 'selected' : ''}>
                                            <td className='text-center'>
                                                <Form.Check
                                                    type="radio"
                                                    name="encounter"
                                                    checked={encounter.encounterId === selectedEncounter?.encounterId}
                                                    onChange={() => handleEncounterChange(encounter)}
                                                />
                                            </td>
                                            <td className='text-left'>{encounter.encounterCode}</td>
                                            <td className='text-left'>{encounter.startDate ? formatDate(encounter.startDate) : '-'}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>
                    {submissionErr && (
                        <div className='ecw-encounterList-modal__error-msg'>{submissionErr}</div>
                    )}
                </>
            }
            footer={
                <div className='ecw-encounterList-modal__actions-wrapper'>
                    <Button variant='outline-primary' onClick={onClose}>Cancel</Button>
                    <Button variant='primary' disabled={!selectedEncounter} onClick={handleSubmit}>Submit</Button>
                </div>
            }
        />
    )
}

export default EcwEncountersModal;